/**
 * Which of a job's legs cannot find the account it names — read from the BACKEND'S verdict (U28).
 *
 * WHY THIS IS A READER AND NOT A JUDGE. The verdict itself is computed in
 * `backend/services/job_leg_health.py`, because deciding it needs the provider registry's
 * declared `account_kind` for the leg's transport AND the actual kind of the referenced row. A
 * second copy of that rule in the browser is how two surfaces come to disagree about whether a
 * job is broken, so this module only reads `needs_attention` and formats it.
 *
 * `not_evaluated` MUST render as nothing. It means the response did not load the account rows —
 * every write path — and the backend sets `needs_attention: false` there for exactly this reason:
 * an "unknown" badged as broken would fire on the 201 of a job that is perfectly fine, and a
 * warning that cries wolf is one nobody reads by the third job.
 */
import type { Job, LegAccountHealth } from '../api/client'

export interface UnhealthyLeg {
  leg: 'outbound' | 'inbound'
  /** Capitalised for prose: "Outbound leg …". */
  label: string
  health: LegAccountHealth
}

/**
 * The legs an operator has something to do about, outbound first.
 *
 * Empty is the normal case, which is what makes it safe for a caller to render a banner
 * unconditionally on a non-empty result.
 */
export function unhealthyLegs(job: Job | null | undefined): UnhealthyLeg[] {
  if (!job) return []
  const out: UnhealthyLeg[] = []
  const pairs: [UnhealthyLeg['leg'], string, LegAccountHealth | null | undefined][] = [
    ['outbound', 'Outbound', job.outbound_account_health],
    ['inbound', 'Inbound', job.inbound_account_health],
  ]
  for (const [leg, label, health] of pairs) {
    if (health && health.needs_attention) out.push({ leg, label, health })
  }
  return out
}

/** True when any leg needs repointing — for a list badge. */
export function jobNeedsAccountRepair(job: Job | null | undefined): boolean {
  return unhealthyLegs(job).length > 0
}

/**
 * The one-line reason for a list cell or a tooltip.
 *
 * Falls back to the status word rather than to an empty string: a badge with no explanation is
 * the dead end this whole signal exists to remove.
 */
export function accountRepairSummary(job: Job | null | undefined): string {
  const legs = unhealthyLegs(job)
  if (legs.length === 0) return ''
  return legs
    .map(({ label, health }) => `${label}: ${health.detail ?? health.status}`)
    .join(' ')
}
